import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getEntitlementPackages } from '../benefits/benefitManageData';
import { getPlanOptions } from '../packages/packageManageData';
import { REGION_OPTIONS } from '../packages/packageFormOptions';
import { OrderDetailModal } from './OrderDetailModal';
import { formatFullTime } from '../../../utils/timeFormat';
import {
  MANAGED_FULFILLMENT_STATUS_LABEL,
  MANAGED_FULFILLMENT_STATUS_OPTIONS,
  MANAGED_ORDER_STATUS_LABEL,
  MANAGED_ORDER_STATUS_OPTIONS,
  MANAGED_ORDER_TYPE_LABEL,
  MANAGED_ORDER_TYPE_OPTIONS,
  MANAGED_PAYMENT_CHANNEL_LABEL,
  MANAGED_PAYMENT_CHANNEL_OPTIONS,
  MANAGED_PAYMENT_STATUS_LABEL,
  MANAGED_PAYMENT_STATUS_OPTIONS,
  formatMinorAmount,
  queryConsoleOrders,
  type ConsoleOrderQuery,
  type ManagedOrder
} from './orderManageData';

const CODE_PATTERN = /^[0-9]{21}$/;

type Filters = {
  orderNo?: string;
  orderType?: string;
  orderStatus?: string;
  fulfillmentStatus?: string;
  planCode?: string;
  packageCode?: string;
  paymentStatus?: string;
  paymentChannel?: string;
};

const emptyFilters: Filters = {};

function formatOrderTime(value?: string | null) {
  const text = formatFullTime(value);
  return text || '—';
}

function dash(value?: string | number | null) {
  if (value == null) return '—';
  const text = String(value).trim();
  return text ? text : '—';
}

function labelOf(map: Record<string, string>, value?: string) {
  if (!value) return '—';
  return map[value] || value;
}

type StatusTone = 'success' | 'pending' | 'danger' | 'muted' | 'review';

function orderStatusTone(status?: string): StatusTone | '' {
  switch (status?.trim()) {
    case 'COMPLETED':
      return 'success';
    case 'PENDING':
      return 'pending';
    case 'CLOSED':
      return 'danger';
    default:
      return '';
  }
}

function paymentStatusTone(status?: string): StatusTone | '' {
  switch (status?.trim()) {
    case 'PAID':
      return 'success';
    case 'UNPAID':
      return 'pending';
    case 'NOT_REQUIRED':
      return 'muted';
    default:
      return '';
  }
}

function fulfillmentStatusTone(status?: string): StatusTone | '' {
  switch (status?.trim()) {
    case 'FULFILLED':
      return 'success';
    case 'PENDING':
      return 'pending';
    case 'MANUAL_REVIEW':
      return 'review';
    default:
      return '';
  }
}

function StatusPill({
  label,
  tone
}: {
  label: string;
  tone?: StatusTone | '';
}) {
  if (!label || label === '—') return <span>—</span>;
  return (
    <span className={`admin-order-status-pill${tone ? ` admin-order-status-pill--${tone}` : ''}`}>
      <i className='admin-order-status-dot' aria-hidden />
      {label}
    </span>
  );
}

function regionText(regionCode?: string) {
  if (!regionCode) return '—';
  const label = REGION_OPTIONS.find((item) => item.value === regionCode)?.label;
  return label ? `${label} ${regionCode}` : regionCode;
}

function amountText(order: ManagedOrder) {
  if (order.amountMinor == null) return '—';
  const amount = formatMinorAmount(order.amountMinor, order.currencyFractionDigits);
  const currency = order.currency?.trim();
  return currency ? `${amount} ${currency}` : amount;
}

function dayText(value?: number | null) {
  if (value == null) return '—';
  return `${value}天`;
}

function readCode(value: string | undefined, label: string) {
  const text = value?.trim() ?? '';
  if (!text) return {};
  if (!CODE_PATTERN.test(text)) return { error: `${label}须为 21 位数字` };
  return { value: text };
}

function buildQuery(filters: Filters, pageNum: number, pageSize: number): ConsoleOrderQuery | string {
  const orderNo = readCode(filters.orderNo, '订单号');
  if (orderNo.error) return orderNo.error;
  const planCode = readCode(filters.planCode, '购买套餐');
  if (planCode.error) return planCode.error;
  const packageCode = readCode(filters.packageCode, '购买权益包');
  if (packageCode.error) return packageCode.error;

  return {
    pageNum,
    pageSize,
    orderNo: orderNo.value,
    orderType: filters.orderType,
    orderStatus: filters.orderStatus,
    fulfillmentStatus: filters.fulfillmentStatus,
    planCode: planCode.value,
    packageCode: packageCode.value,
    paymentStatus: filters.paymentStatus,
    paymentChannel: filters.paymentChannel
  };
}

export function OrderManage() {
  const [form] = Form.useForm<Filters>();
  const [rows, setRows] = useState<ManagedOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [applied, setApplied] = useState<Filters>(emptyFilters);
  const [loading, setLoading] = useState(false);
  const [detailOrderNo, setDetailOrderNo] = useState<string | null>(null);
  const [planOptions, setPlanOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [packageOptions, setPackageOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [packageLoading, setPackageLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadPlans = async () => {
      setPlanLoading(true);
      const res = await getPlanOptions();
      if (cancelled) return;
      if (res.code !== 200 || !Array.isArray(res.content)) {
        if (res.code !== -1) message.error(res.message || '套餐列表加载失败');
        setPlanOptions([]);
        return;
      }
      const collected: Array<{ value: string; label: string }> = [];
      res.content.forEach((item) => {
        const planCode = item.planCode?.trim() ?? '';
        if (!CODE_PATTERN.test(planCode)) return;
        collected.push({
          value: planCode,
          label: item.displayName?.trim() || planCode
        });
      });
      if (!cancelled) setPlanOptions(collected);
    };
    loadPlans()
      .catch(() => {
        if (!cancelled) message.error('套餐列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setPlanLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPackages = async () => {
      setPackageLoading(true);
      const collected: Array<{ value: string; label: string }> = [];
      for (let page = 1; page <= 10; page += 1) {
        const res = await getEntitlementPackages({ pageNum: page, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '权益包列表加载失败');
          break;
        }
        records.forEach((item) => {
          const packageCode = item.packageCode?.trim() ?? '';
          if (!CODE_PATTERN.test(packageCode)) return;
          collected.push({
            value: packageCode,
            label: item.displayName?.trim() || packageCode
          });
        });
        if (records.length < 100) break;
      }
      if (!cancelled) setPackageOptions(collected);
    };
    loadPackages()
      .catch(() => {
        if (!cancelled) message.error('权益包列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setPackageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const query = buildQuery(applied, pageNum, pageSize);
    if (typeof query === 'string') return;
    let cancelled = false;
    setLoading(true);
    queryConsoleOrders(query)
      .then((res) => {
        if (cancelled) return;
        const page = res.content;
        if (res.code !== 200 || !page || !Array.isArray(page.records)) {
          if (res.code !== -1) message.error(res.message || '订单列表加载失败');
          setRows([]);
          setTotal(0);
          return;
        }
        setRows(page.records);
        setTotal(typeof page.total === 'number' ? page.total : page.records.length);
      })
      .catch(() => {
        if (!cancelled) message.error('订单列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applied, pageNum, pageSize]);

  const handleQuery = () => {
    const values = form.getFieldsValue();
    const query = buildQuery(values, 1, pageSize);
    if (typeof query === 'string') {
      message.error(query);
      return;
    }
    setPageNum(1);
    setApplied(values);
  };

  const handleReset = () => {
    form.resetFields();
    setPageNum(1);
    setApplied(emptyFilters);
  };

  const columns: ColumnsType<ManagedOrder> = [
    {
      title: '序号',
      width: 64,
      fixed: 'left',
      render: (_, __, index) => (pageNum - 1) * pageSize + index + 1
    },
    {
      title: '订单号',
      dataIndex: 'orderNo',
      width: 200,
      render: (value?: string) => dash(value)
    },
    {
      title: '用户编码',
      dataIndex: 'userCode',
      width: 200,
      render: (value?: string) => dash(value)
    },
    {
      title: '用户名称',
      dataIndex: 'userName',
      width: 120,
      render: (value?: string) => dash(value)
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 150,
      render: (value?: string) => dash(value)
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 180,
      ellipsis: true,
      render: (value?: string) => dash(value)
    },
    {
      title: '地区',
      dataIndex: 'regionCode',
      width: 140,
      render: (value?: string) => regionText(value)
    },
    {
      title: '订单类型',
      dataIndex: 'orderType',
      width: 120,
      render: (value?: string) => labelOf(MANAGED_ORDER_TYPE_LABEL, value)
    },
    {
      title: '商品名称',
      dataIndex: 'productName',
      width: 180,
      ellipsis: true,
      render: (value?: string) => dash(value)
    },
    {
      title: '套餐天数',
      dataIndex: 'durationDays',
      width: 96,
      render: (value?: number | null) => dayText(value)
    },
    {
      title: '权益天数',
      dataIndex: 'validityDays',
      width: 96,
      render: (value?: number | null) => dayText(value)
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      width: 72,
      render: (value?: number) => (value == null ? '—' : value)
    },
    {
      title: '订单金额',
      key: 'amount',
      width: 140,
      render: (_, row) => amountText(row)
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      width: 110,
      render: (value?: string) => (
        <StatusPill label={labelOf(MANAGED_ORDER_STATUS_LABEL, value)} tone={orderStatusTone(value)} />
      )
    },
    {
      title: '支付状态',
      dataIndex: 'paymentStatus',
      width: 110,
      render: (value?: string) => (
        <StatusPill label={labelOf(MANAGED_PAYMENT_STATUS_LABEL, value)} tone={paymentStatusTone(value)} />
      )
    },
    {
      title: '发放状态',
      dataIndex: 'fulfillmentStatus',
      width: 120,
      render: (value?: string) => (
        <StatusPill
          label={labelOf(MANAGED_FULFILLMENT_STATUS_LABEL, value)}
          tone={fulfillmentStatusTone(value)}
        />
      )
    },
    {
      title: '支付渠道',
      dataIndex: 'paymentChannel',
      width: 110,
      render: (value?: string) => labelOf(MANAGED_PAYMENT_CHANNEL_LABEL, value)
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (value?: string) => formatOrderTime(value)
    },
    {
      title: '支付时间',
      dataIndex: 'paidAt',
      width: 180,
      render: (value?: string) => formatOrderTime(value)
    },
    {
      title: '关闭时间',
      dataIndex: 'closedAt',
      width: 180,
      render: (value?: string) => formatOrderTime(value)
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, row) => (
        <button
          type='button'
          className='admin-link-btn'
          onClick={() => {
            const orderNo = row.orderNo?.trim() ?? '';
            if (!CODE_PATTERN.test(orderNo)) {
              message.warning('订单号须为 21 位数字');
              return;
            }
            setDetailOrderNo(orderNo);
          }}
        >
          详情
        </button>
      )
    }
  ];

  return (
    <div className='admin-page-card'>
      <Form
        form={form}
        layout='horizontal'
        labelAlign='right'
        colon
        className='admin-order-filter'
        onFinish={handleQuery}
      >
        <div className='admin-order-filter-grid'>
          <Form.Item name='orderNo' label='订单号'>
            <Input allowClear placeholder='21 位订单号' />
          </Form.Item>
          <Form.Item name='orderType' label='订单类型'>
            <Select allowClear placeholder='全部' options={MANAGED_ORDER_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name='orderStatus' label='订单状态'>
            <Select allowClear placeholder='全部' options={MANAGED_ORDER_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name='fulfillmentStatus' label='发放状态'>
            <Select allowClear placeholder='全部' options={MANAGED_FULFILLMENT_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name='planCode' label='购买套餐'>
            <Select
              allowClear
              showSearch
              optionFilterProp='label'
              placeholder='全部'
              loading={planLoading}
              options={planOptions}
            />
          </Form.Item>
          <Form.Item name='packageCode' label='购买权益包'>
            <Select
              allowClear
              showSearch
              optionFilterProp='label'
              placeholder='全部'
              loading={packageLoading}
              options={packageOptions}
            />
          </Form.Item>
          <Form.Item name='paymentStatus' label='支付状态'>
            <Select allowClear placeholder='全部' options={MANAGED_PAYMENT_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name='paymentChannel' label='支付渠道'>
            <Select allowClear placeholder='全部' options={MANAGED_PAYMENT_CHANNEL_OPTIONS} />
          </Form.Item>
        </div>
        <div className='admin-order-filter-actions'>
          <Button onClick={handleReset}>重置</Button>
          <Button type='primary' htmlType='submit' className='admin-pkg-query-btn'>
            查询
          </Button>
        </div>
      </Form>

      <Table<ManagedOrder>
        rowKey={(row, index) => row.orderNo || `order-${index}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        size='middle'
        className='admin-user-table admin-order-table'
        scroll={{ x: 2600 }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (count) => {
            const pages = Math.max(1, Math.ceil(count / pageSize));
            return `共${pages}页`;
          },
          onChange: (current, size) => {
            if (size !== pageSize) {
              setPageNum(1);
              setPageSize(size);
              return;
            }
            setPageNum(current);
          }
        }}
      />

      <OrderDetailModal open={Boolean(detailOrderNo)} orderNo={detailOrderNo} onClose={() => setDetailOrderNo(null)} />
    </div>
  );
}
